import { useEffect, useState } from "react";
import { Cloud, Server, Settings2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import type { TFunction } from "i18next";

import { SectionHead } from "@/components/brand/Card";
import type { PillTone } from "@/components/brand/Pill";
import { ConnectionStatusCard } from "@/components/projects/ConnectionStatusCard";
import { TargetConnectionCard } from "@/components/projects/TargetConnectionCard";
import {
  TargetConnectionSheet,
  type TargetSheetSubTab,
  type TargetSheetTab,
} from "@/components/projects/TargetConnectionSheet";
import { useTargetConnection, useTestTargetConnection } from "@/hooks/useApi";
import { useCbcConnection, useTestCbcConnection } from "@/hooks/useConfigMigration";
import { useToast } from "@/hooks/use-toast";
import { errorDetailOr } from "@/lib/api-error";
import type { ProjectTargetConnection } from "@/types/api";

interface ModalState {
  open: boolean;
  tab: TargetSheetTab;
  subTab: TargetSheetSubTab;
}

type StatusVisual = { tone: PillTone; label: string; accent: string };

const NOT_CONFIGURED_ACCENT = "#ffc83d";

function targetStatus(
  t: TFunction,
  data: ProjectTargetConnection | undefined,
): { configured: boolean; visual: StatusVisual } {
  const configured = !!(data && data.status && data.configured !== false);
  if (!configured) {
    return {
      configured,
      visual: { tone: "muted", label: t("Not configured"), accent: "#b2bccb" },
    };
  }
  if (data?.status === "connected") {
    return { configured, visual: { tone: "ok", label: t("Connected"), accent: "#27a155" } };
  }
  if (data?.status === "failed") {
    return { configured, visual: { tone: "danger", label: t("Failed"), accent: "#d4353c" } };
  }
  return {
    configured,
    visual: { tone: "gold", label: t("Configured"), accent: NOT_CONFIGURED_ACCENT },
  };
}

/** Seção "Target system" de /connections, com a mesma lógica da seção Source: sem nenhuma conexão
 * do alvo, o card grande; com pelo menos uma, só os cards pequenos das que existem (Development,
 * Configuration, CBC). Um único modal cadastra e edita todas; quem o abre decide só em qual aba
 * ele começa. */
export function TargetSystemSection({
  projectId,
  addOpen,
  onAddOpenChange,
}: {
  projectId: string;
  /** Pedido de fora (o "Add connection" do topo da página, depois de escolher Target) para abrir
   * o modal em Connection > Development. A seção o consome e devolve `false`. */
  addOpen?: boolean;
  onAddOpenChange?: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [modal, setModal] = useState<ModalState>({
    open: false,
    tab: "connection",
    subTab: "development",
  });
  const openModal = (tab: TargetSheetTab, subTab: TargetSheetSubTab = "development") =>
    setModal({ open: true, tab, subTab });

  useEffect(() => {
    if (!addOpen) return;
    setModal({ open: true, tab: "connection", subTab: "development" });
    onAddOpenChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só reage ao pedido; o callback do dono pode mudar a cada render
  }, [addOpen]);

  // Atalho das recusas do play (`?target=cbc` ou `?target=configuration`): abre o modal na
  // aba que resolve e tira o parâmetro, para o voltar do navegador não reabrir.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const target = searchParams.get("target");
    if (target !== "cbc" && target !== "configuration") return;
    setModal(
      target === "cbc"
        ? { open: true, tab: "cbc", subTab: "development" }
        : { open: true, tab: "connection", subTab: "configuration" },
    );
    const next = new URLSearchParams(searchParams);
    next.delete("target");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data: dev, isLoading: devLoading } = useTargetConnection(projectId, "development");
  const { data: conf, isLoading: confLoading } = useTargetConnection(projectId, "configuration");
  const {
    data: cbc,
    isLoading: cbcLoading,
    isError: cbcError,
    refetch: refetchCbc,
  } = useCbcConnection(projectId);
  const testDev = useTestTargetConnection(projectId, "development");
  const testCbc = useTestCbcConnection(projectId);

  const devState = targetStatus(t, dev);
  const confState = targetStatus(t, conf);
  const cbcConfigured = !!cbc?.configured;
  // O CBC não tem coluna de status: "Connected" é ter feito login com sucesso (`last_login_at`,
  // zerado quando a credencial muda); com conexão gravada e sem login válido, é "Configured".
  // Tudo do CBC aqui é de quem está vendo: a API devolve só a conexão dele.
  const cbcLastLogin = cbc?.last_login_at ?? null;
  const cbcVisual: StatusVisual = cbcLastLogin
    ? { tone: "ok", label: t("Connected"), accent: "#27a155" }
    : { tone: "gold", label: t("Configured"), accent: NOT_CONFIGURED_ACCENT };
  const loading = devLoading || confLoading || cbcLoading;
  // Com o GET do CBC em erro não se sabe se há conexão: o card de erro precisa aparecer, em vez
  // de o card grande (ou o "Set up your CBC") levar a refazer o cadastro às cegas.
  const cbcFailed = cbcError && !cbc;
  const anyConfigured = devState.configured || confState.configured || cbcConfigured || cbcFailed;

  const reportError = (title: string, err: unknown) =>
    toast({
      title,
      description: errorDetailOr(err, "") || undefined,
      variant: "destructive",
    });

  const handleTestDev = () =>
    testDev.mutate(undefined, {
      onSuccess: (result) =>
        toast({
          title: result.success ? t("Connection test passed") : t("Connection test failed"),
          description: result.message,
          variant: result.success ? undefined : "destructive",
        }),
      onError: (err) => reportError(t("Could not test the connection"), err),
    });

  // Sem `password`: o servidor mantém a senha já gravada no cofre.
  const handleTestCbc = () =>
    testCbc.mutate(
      { base_url: cbc?.base_url ?? "", username: cbc?.username ?? "" },
      {
        onSuccess: (result) =>
          toast({
            title: result.success ? t("Connection test passed") : t("Connection test failed"),
            description: result.message,
            variant: result.success ? undefined : "destructive",
          }),
        onError: (err) => reportError(t("Could not test the connection"), err),
      },
    );

  return (
    <>
      <SectionHead overline={t("TARGET")} title={t("Target system")} />
      {loading ? null : !anyConfigured ? (
        // Sem nenhuma conexão do alvo: o card grande, como a seção Source mostra quando não há ECC.
        <TargetConnectionCard
          projectId={projectId}
          onConfigure={() => openModal("connection", "development")}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {devState.configured && (
            <ConnectionStatusCard
              id="development"
              icon={<Server className="w-5 h-5" />}
              overline={`${t("TARGET")} · ${t("Development")}`}
              title={t("Development")}
              status={devState.visual}
              rows={[
                { k: t("URL"), v: dev?.base_url || "—", mono: true },
                { k: t("Release"), v: dev?.release || "—", mono: true },
                { k: t("Client"), v: dev?.client || "—", mono: true },
                { k: t("SWC"), v: dev?.software_component || "—", mono: true },
                { k: t("Credentials"), v: dev?.has_credentials ? t("Configured") : t("Missing") },
              ]}
              onEdit={() => openModal("connection", "development")}
              test={{
                onClick: handleTestDev,
                disabled: !dev?.has_credentials,
                pending: testDev.isPending,
                hint: t("Add the credentials before testing"),
              }}
            />
          )}
          {confState.configured && (
            <ConnectionStatusCard
              id="configuration"
              icon={<Settings2 className="w-5 h-5" />}
              overline={`${t("TARGET")} · ${t("Configuration")}`}
              title={t("Configuration")}
              status={confState.visual}
              rows={[
                { k: t("URL"), v: conf?.base_url || "—", mono: true },
                { k: t("Client"), v: conf?.client || "—", mono: true },
              ]}
              onEdit={() => openModal("connection", "configuration")}
            />
          )}
          {cbcConfigured ? (
            <ConnectionStatusCard
              id="cbc"
              icon={<Cloud className="w-5 h-5" />}
              overline={`${t("TARGET")} · ${t("CBC")}`}
              title={t("CBC")}
              status={cbcVisual}
              rows={[
                { k: t("URL"), v: cbc?.base_url || "—", mono: true },
                { k: t("User"), v: cbc?.username || "—", mono: true },
                { k: t("Credentials"), v: cbc?.has_credentials ? t("Configured") : t("Missing") },
              ]}
              lastTested={cbcLastLogin}
              onEdit={() => openModal("cbc")}
              test={{
                onClick: handleTestCbc,
                disabled: !cbc?.base_url || !cbc?.username,
                pending: testCbc.isPending,
                hint: t("Save the URL and user before testing"),
              }}
            />
          ) : cbcFailed ? (
            <ConnectionStatusCard
              id="cbc"
              icon={<Cloud className="w-5 h-5" />}
              overline={`${t("TARGET")} · ${t("CBC")}`}
              title={t("CBC")}
              status={{ tone: "danger", label: t("Could not load your CBC connection"), accent: "#d4353c" }}
              rows={[]}
              onEdit={() => void refetchCbc()}
              editLabel={t("Retry")}
            />
          ) : (
            // O CBC é de cada usuário: quem ainda não cadastrou o dele vê o card vazio com
            // o botão de cadastrar, mesmo que outra pessoa do projeto tenha o seu.
            <ConnectionStatusCard
              id="cbc"
              icon={<Cloud className="w-5 h-5" />}
              overline={`${t("TARGET")} · ${t("CBC")}`}
              title={t("CBC")}
              status={{ tone: "muted", label: t("Not configured"), accent: "#b2bccb" }}
              rows={[
                { k: t("URL"), v: "—", mono: true },
                { k: t("User"), v: "—", mono: true },
              ]}
              onEdit={() => openModal("cbc")}
              editLabel={t("Set up your CBC")}
            />
          )}
        </div>
      )}
      <TargetConnectionSheet
        projectId={projectId}
        open={modal.open}
        onOpenChange={(open) => setModal((m) => ({ ...m, open }))}
        initialTab={modal.tab}
        initialSubTab={modal.subTab}
      />
    </>
  );
}
